import { useRef, useState } from 'react';
import { Buffer } from 'buffer';
import {
    Flex,
    MenuButton,
    Button,
    MenuList,
    MenuItem,
    Icon,
    Switch,
    InputGroup,
    Input,
    InputRightElement,
    IconButton,
    Text,
    Menu,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
    Checkbox,
    useToast,
    Spinner,
    Box,
} from '@chakra-ui/react';
import icons from 'shared/icons';
import {
    TPictures,
    useDeletePicturesMutation,
    useLazyDownloadPicturesQuery,
    useUpdatePicturesMutation,
} from 'core/API/picturesApi';
import { useTranslation } from 'helpers/languages/translations';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { sortTypes } from 'pages/ImageManagement/contants';
import { iconTableStyle } from 'shared/styles';
import { TImagesViewer } from './ImagesViewerContainer';
import { entries } from 'utils/wrappers';
import ToggleRadioButton from 'components/molecules/ToggleRadioButton';
import { ChevronDownIcon } from '@chakra-ui/icons';

const ADMIN_ID = 1;
const MAX_DOWNLOAD_FILES = 500;

export type TStatus = 'active' | 'archived' | 'other';

export const switchFilter: Record<TStatus, Partial<TPictures>> = {
    active: { status: 'active', isWarning: false },
    archived: { status: 'archived', isWarning: undefined },
    other: { isWarning: true, status: 'active' },
};

export const Actions = ({
    currentStatus,
    checkedPictures,
    callback,
    readonly,
}: {
    currentStatus: TStatus;
    checkedPictures: string[];
    callback?: () => void;
    readonly: boolean;
}) => {
    const translate = useTranslation();
    const toast = useToast();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const userInfo = useGetUserInfoSelector();
    const isDelete = useRef(false);

    const [deletePicturesQuery] = useDeletePicturesMutation();
    const [updatePicturesQuery] = useUpdatePicturesMutation();

    const onArchiveOrDelete = async (action: 'delete' | 'archive' | 'unarchive', checkedPictures: string[]) => {
        let resp: any;

        if (/archive/.test(action)) {
            resp = await updatePicturesQuery({
                pictureIds: checkedPictures,
                updates: { status: action === 'archive' ? 'archived' : 'active' },
            });
        } else {
            resp = await deletePicturesQuery({ ids: checkedPictures, isDelete: action === 'delete' });
        }

        const isError = 'error' in resp;

        toast({
            title: translate(isError ? 'error.generic' : `pictures.${action}.success`),
            status: isError ? 'error' : 'success',
        });
    };

    const action = isDelete.current ? 'delete' : currentStatus === 'active' ? 'archive' : 'unarchive';

    return (
        <>
            {currentStatus !== 'other' && (
                <IconButton
                    mr={2}
                    variant="outline"
                    aria-label="archive images"
                    size="md"
                    icon={<Icon as={currentStatus === 'archived' ? icons.check : icons.archive} />}
                    isDisabled={!checkedPictures?.length || readonly}
                    onClick={() => {
                        isDelete.current = false;
                        onOpen();
                    }}
                />
            )}
            {userInfo?.user_groups.includes(ADMIN_ID) && (
                <IconButton
                    variant="outline"
                    aria-label="delete images"
                    size="md"
                    color="red"
                    borderColor="red"
                    icon={<Icon as={icons.delete} />}
                    isDisabled={!checkedPictures?.length || readonly}
                    onClick={() => {
                        isDelete.current = true;
                        onOpen();
                    }}
                />
            )}
            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text as="h3">{translate('pictures.deleteModal.title')}</Text>
                    </ModalHeader>
                    <ModalBody fontSize="sm">
                        <Button mr={3} variant="ghost" onClick={onClose}>
                            {translate('pictures.deleteModal.cancel')}
                        </Button>
                        <Button
                            colorScheme="blue"
                            onClick={async () => {
                                onClose();

                                const action = isDelete.current
                                    ? 'delete'
                                    : currentStatus === 'active'
                                    ? 'archive'
                                    : 'unarchive';

                                await onArchiveOrDelete(action, checkedPictures);
                                callback?.();
                            }}
                        >
                            {translate(`pictures.deleteModal.${action}`)}
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
};

const ToolBar = ({
    isGrouped,
    choosenSort,
    isAscendingOrder,
    nbPictures,
    inputFilter,
    filtersList,
    checkedPictures,
    setChoosenSort,
    toggleAscending,
    setIsGrouped,
    setInputFilter,
    setFilters,
    setCheckedPicture,
    readonly,
}: TImagesViewer) => {
    const translate = useTranslation();
    const toast = useToast();
    const companyInfo = useGetCompanyInfoSelector();
    const currentStatus = useRef<TStatus>('active');
    const [downloadPicturesQuery] = useLazyDownloadPicturesQuery();

    const [isDownloading, setIsDownloading] = useState(false);

    const onRadioClick = (value: TStatus) => {
        currentStatus.current = value;
        setCheckedPicture([], false);
        setFilters((f) => ({ ...f, ...switchFilter[value] }));
    };

    const download = async () => {
        setIsDownloading(true);

        if (checkedPictures.length > MAX_DOWNLOAD_FILES) {
            toast({
                title: translate(`pictures.downloadSize.error`, { limit: MAX_DOWNLOAD_FILES }),
                status: 'warning',
            });
            setIsDownloading(false);
            return;
        }

        const resp: any = await downloadPicturesQuery({
            ids: checkedPictures,
        });

        const buffer = Buffer.from(resp.data.buffer, 'base64');
        const blob = new Blob([buffer]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${companyInfo?.country_id}_${companyInfo?.id}.zip`;

        document.body.appendChild(link);

        setIsDownloading(false);

        link.click();
        link.parentNode?.removeChild(link);
    };

    return (
        <>
            <Flex justifyContent="space-between">
                <Flex alignItems="center">
                    <InputGroup size="md" w="200px" mr="16px">
                        <Input
                            bg="white"
                            size="md"
                            placeholder="Search"
                            onChange={(e) => setInputFilter(e.target.value)}
                            value={inputFilter}
                        />
                        <InputRightElement children={<icons.search />} />
                    </InputGroup>
                    <Menu>
                        <MenuButton
                            as={IconButton}
                            {...iconTableStyle}
                            aria-label="filter images"
                            size="md"
                            icon={<Icon as={icons.filter} />}
                        >
                            {choosenSort.title}
                        </MenuButton>
                        <MenuList fontWeight="400" fontSize="14px" color="gray.640" px={6} py={4} w="350px">
                            {entries(filtersList).map(([title, filter]) => (
                                <Flex alignItems="center" key={title}>
                                    <Text flexGrow={1}>{translate(`pictures.filters.${title}`)}</Text>
                                    <Box>{filter}</Box>
                                </Flex>
                            ))}
                        </MenuList>
                    </Menu>
                    <IconButton
                        variant="outline"
                        aria-label="dowload images"
                        size="md"
                        icon={isDownloading ? <Spinner /> : <Icon as={icons.download} />}
                        mr={2}
                        onClick={download}
                        isDisabled={!checkedPictures.length || isDownloading}
                    />
                    <Actions
                        currentStatus={currentStatus.current}
                        checkedPictures={checkedPictures}
                        callback={() => setCheckedPicture([], false)}
                        readonly={readonly}
                    />
                </Flex>
                <ToggleRadioButton
                    items={[
                        {
                            content: <Icon as={icons.check} />,
                            onClick: () => onRadioClick('active'),
                            tooltip: translate('pictures.active') as string,
                            bubble: nbPictures.active as number,
                        },
                        {
                            content: <Icon as={icons.archive} />,
                            onClick: () => onRadioClick('archived'),
                            tooltip: translate('pictures.archived') as string,
                            bubble: nbPictures.archived as number,
                        },
                        {
                            content: <Icon as={icons.warning} />,
                            onClick: () => onRadioClick('other'),
                            tooltip: `${translate('pictures.upload.no_product')} /
                            ${translate('pictures.upload.wrong_company')}`,
                            bubble: nbPictures.other as number,
                        },
                    ]}
                />
                <Flex alignItems="center">
                    {nbPictures.current}
                    <Icon as={icons.image} mx={2} />
                    {`${checkedPictures.length > 0 ? `(${checkedPictures.length} selected)` : ''}`}
                </Flex>
                <Flex alignItems="center">
                    <Text fontSize="14px" color="gray.640" fontWeight="600" mr={2}>
                        {translate('pictures.filters.groupByEan')}
                    </Text>
                    <Switch
                        id="email-alerts"
                        mr={3}
                        onChange={(e) => setIsGrouped(e.target.checked)}
                        isChecked={isGrouped}
                    />
                    <Text fontSize="14px" color="gray.640" fontWeight="600" mr={2}>
                        {translate('pictures.filters.sort')}
                    </Text>
                    <Flex w="200px" mr="4">
                        <Menu matchWidth>
                            <MenuButton
                                as={Button}
                                variant="outline"
                                w="100%"
                                color="gray.640"
                                textAlign="start"
                                fontWeight="400"
                                fontSize="14px"
                                bg="white"
                                rightIcon={<ChevronDownIcon />}
                            >
                                {choosenSort.title}
                            </MenuButton>
                            <MenuList fontWeight="400" fontSize="14px" minW="200px">
                                {entries(sortTypes).map(([key, sortType]) => (
                                    <MenuItem key={key} onClick={() => setChoosenSort(sortType)} value={key}>
                                        {sortType.title}
                                    </MenuItem>
                                ))}
                            </MenuList>
                        </Menu>
                    </Flex>
                    <Flex onClick={toggleAscending}>
                        <Icon as={isAscendingOrder ? icons.arrowDown : icons.arrowUp} />
                    </Flex>
                </Flex>
            </Flex>
            <Flex flex={1} justifyContent="end" mt={3}>
                <Checkbox
                    isChecked={
                        checkedPictures.length > 0 && checkedPictures.length === nbPictures[currentStatus.current]
                    }
                    onChange={(e) => setCheckedPicture([], e.target.checked)}
                >
                    {translate('pictures.selectAll')}
                </Checkbox>
            </Flex>
        </>
    );
};

export default ToolBar;
